import React, { useState } from 'react';
import { Share2, Facebook, Twitter, Mail, Link as LinkIcon, MessageCircle, Check } from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { useLanguage } from '../contexts/LanguageContext';

const ShareButton = ({ title, description, url, image }) => {
  const [copied, setCopied] = useState(false);
  const { t } = useLanguage();
  
  const shareUrl = url || window.location.href;
  const shareTitle = title || 'Check this out!';
  const shareText = description || 'Amazing travel experience from Exciting.Travel';

  // Native Web Share API (for mobile)
  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: shareTitle,
          text: shareText,
          url: shareUrl,
        });
      } catch (err) {
        console.log('Share cancelled or failed');
      }
    }
  };

  // Copy link to clipboard
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy link');
    }
  };

  // Social media share URLs
  const shareLinks = {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
    twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareTitle)}`,
    whatsapp: `https://wa.me/?text=${encodeURIComponent(shareTitle + ' ' + shareUrl)}`,
    email: `mailto:?subject=${encodeURIComponent(shareTitle)}&body=${encodeURIComponent(shareText + '\n\n' + shareUrl)}`,
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="border-2 hover:border-[#84bc00] hover:bg-[#84bc00]/10 transition-all duration-300"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <Share2 size={16} className="mr-2" />
          {t('share.button')}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        {/* Native Share (Mobile) */}
        {navigator.share && (
          <DropdownMenuItem
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleNativeShare();
            }}
            className="cursor-pointer"
          >
            <Share2 size={16} className="mr-2 text-[#84bc00]" />
            {t('share.shareVia')}
          </DropdownMenuItem>
        )}

        {/* Copy Link */}
        <DropdownMenuItem
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleCopyLink();
          }}
          className="cursor-pointer"
        >
          {copied ? (
            <>
              <Check size={16} className="mr-2 text-[#84bc00]" />
              {t('share.linkCopied')}
            </>
          ) : (
            <>
              <LinkIcon size={16} className="mr-2 text-[#84bc00]" />
              {t('share.copyLink')}
            </>
          )}
        </DropdownMenuItem>

        {/* Facebook */}
        <DropdownMenuItem
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            window.open(shareLinks.facebook, '_blank', 'width=600,height=400');
          }}
          className="cursor-pointer"
        >
          <Facebook size={16} className="mr-2 text-blue-600" />
          {t('share.facebook')}
        </DropdownMenuItem>

        {/* Twitter */}
        <DropdownMenuItem
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            window.open(shareLinks.twitter, '_blank', 'width=600,height=400');
          }}
          className="cursor-pointer"
        >
          <Twitter size={16} className="mr-2 text-sky-500" />
          {t('share.twitter')}
        </DropdownMenuItem>

        {/* WhatsApp */}
        <DropdownMenuItem
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            window.open(shareLinks.whatsapp, '_blank', 'width=600,height=400');
          }}
          className="cursor-pointer"
        >
          <MessageCircle size={16} className="mr-2 text-green-600" />
          {t('share.whatsapp')}
        </DropdownMenuItem>

        {/* Email */}
        <DropdownMenuItem
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            window.location.href = shareLinks.email;
          }}
          className="cursor-pointer"
        >
          <Mail size={16} className="mr-2 text-gray-600" />
          {t('share.email')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ShareButton;
